import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AuthForm from "@/components/AuthForm";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import usePageTitle from "@/hooks/usePageTitle";

const Auth = () => {
  // Set page title
  usePageTitle('Sign In');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingLink, setIsProcessingLink] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    const handleAuth = async () => {
      // First, check if we're already authenticated
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // If we already have a session and we're on the auth page, redirect to submit-app
        if (session && location.pathname === "/auth" && !location.hash) {
          console.log("Already authenticated, redirecting to submit-app");
          navigate("/submit-app", { replace: true });
          return;
        }
        
        // If we have a hash with access_token, process it
        if (location.hash && location.hash.includes('access_token')) {
          console.log("Processing auth token from hash...");
          setIsProcessingLink(true);
          
          try {
            // This should update the session with the token from the hash
            await supabase.auth.getSession();
            
            // Verify that we now have a valid session
            const { data: { session: updatedSession }, error } = await supabase.auth.getSession();
            
            if (error) throw error;
            
            if (updatedSession) {
              console.log("Authentication successful, session established");
              toast({
                title: "Successfully signed in",
                description: "You have been signed in successfully.",
              });
              
              // Redirect to submit-app after successful authentication
              navigate("/submit-app", { replace: true });
              return;
            } else {
              // If we still don't have a session after processing the hash
              console.error("No session after processing hash");
              toast({
                title: "Authentication failed",
                description: "Unable to establish a session. Please try again.",
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error('Error processing auth token:', error);
            toast({
              title: "Error signing in",
              description: "There was an error processing your login. Please try again.",
              variant: "destructive",
            });
          } finally {
            setIsProcessingLink(false);
          }
        }
      } catch (error) {
        console.error('Error in auth flow:', error);
      } finally {
        setIsLoading(false);
        setSessionChecked(true);
      }
    };

    handleAuth();

    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event, !!session);
      
      if (event === 'SIGNED_IN' && session) {
        // Only redirect if we're not already processing a sign-in
        if (!isProcessingLink) {
          navigate("/submit-app", { replace: true });
        }
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate, location, toast, isProcessingLink]);

  // Show processing state
  if (isLoading || isProcessingLink) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation />
        <div className="container py-12 flex justify-center items-center flex-grow">
          <p className="text-lg">{isProcessingLink ? "Processing your login..." : "Loading..."}</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Show auth form
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navigation />
      <div className="container max-w-md mx-auto py-12 flex-grow">
        <div className="bg-white rounded-lg border p-6">
          <h1 className="text-2xl font-bold mb-4 text-center">Sign in to submit your app</h1>
          <AuthForm onAuthSuccess={() => navigate("/submit-app")} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Auth;
